import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  Calendar,
  DollarSign,
} from "lucide-react";
import { format } from "date-fns";

import DashboardContainer from "@/components/layout/DashboardContainer";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  ClientDashboardData,
  GetClientInvoicesResponse,
  ClientInvoice,
} from "@/types/client";

export function ClientInvoiceManagement({
  onViewInvoice,
}: {
  onViewInvoice: (invoice: ClientInvoice) => void;
}) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<ClientDashboardData | null>(null);
  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
    fetchInvoices();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await ApiService.request<ClientDashboardData>(
        EndpointKeys.GET_CLIENT_DASHBOARD_DATA,
        {
          method: "GET",
        },
      );
      setDashboardData(response);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      setFetchError(null);

      const response = await ApiService.request<GetClientInvoicesResponse>(
        EndpointKeys.GET_CLIENT_INVOICES,
        {
          method: "GET",
        },
      );

      // Filter out DRAFT invoices - clients should only see submitted invoices
      const submittedInvoices = response.invoices.filter(invoice => invoice.status !== "DRAFT");
      setInvoices(submittedInvoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Failed to load invoices";
      setFetchError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "APPROVED":
        return "default";
      case "SUBMITTED":
        return "secondary";
      case "DRAFT":
        return "outline";
      case "REJECTED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "APPROVED":
        return <CheckCircle className="h-4 w-4" />;
      case "SUBMITTED":
        return <Clock className="h-4 w-4" />;
      case "REJECTED":
        return <XCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const pendingInvoices = invoices.filter(inv => inv.status === "SUBMITTED");

  return (
    <DashboardContainer title="Invoice Management">
      <div className="space-y-6">
        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Approvals
              </CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardData?.pendingInvoicesCount || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Invoices awaiting your approval
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Invoices
              </CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardData?.totalInvoices || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                All invoices received
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Amount
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(
                  pendingInvoices.reduce((sum, inv) => sum + (parseFloat(inv.grandTotal.toString()) || 0), 0)
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Total pending approval
              </p>
            </CardContent>
          </Card>
        </div>

        {fetchError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{fetchError}</AlertDescription>
          </Alert>
        )}

        {/* Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-4">
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </div>
                ))}
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Invoices</h3>
                <p className="text-muted-foreground">
                  You haven't received any invoices yet.
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        className="hover:bg-muted/50 cursor-pointer"
                        data-testid={`client-invoice-row-${invoice.id}`}
                        onClick={() => onViewInvoice(invoice)}
                      >
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(invoice.status)}
                            <div>
                              <div className="font-medium">
                                {invoice.invoiceNumber || `Invoice #${invoice.id}`}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Created {format(new Date(invoice.createdAt), "dd/MM/yyyy")}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>{format(new Date(invoice.dueDate), "dd/MM/yyyy")}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(invoice.status)}>
                            {formatStatus(invoice.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(parseFloat(invoice.grandTotal.toString()) || 0)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardContainer>
  );
} 